// src/screens/Income.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Sidebar Data Entry Tool — Income
// Pre-session capture of income fields. Writes to the same ClientData fields
// that the Tax Check screen reads from, so figures are pre-populated.
//
// When isCouple === true, renders a two-column side-by-side layout:
//   Left column  = client income fields
//   Right column = partner income fields
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import { ClientData } from '../types/ClientData';
import CurrencyInput from '../components/CurrencyInput';
import { formatCurrency } from '../utils/format';

interface Props {
  data: ClientData;
  onUpdate: (updates: Partial<ClientData>) => void;
}

const fmt = formatCurrency;

// ── Shared sub-components ────────────────────────────────────────────────────

function TaxMethodPicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (v: 'monthly' | 'selfAssessment') => void;
}) {
  return (
    <div className="flex gap-3">
      {([
        { key: 'monthly' as const, label: 'Monthly SO', desc: 'Standing order to HMRC' },
        { key: 'selfAssessment' as const, label: 'January & July SA', desc: 'Self-assessment payments on account' },
      ]).map(opt => (
        <button
          key={opt.key}
          onClick={() => onChange(opt.key)}
          className={`flex-1 p-3 rounded-xl border-2 text-left transition ${
            value === opt.key
              ? 'border-ma-navy bg-blue-50'
              : 'border-gray-200 bg-white hover:border-gray-300'
          }`}
        >
          <div className={`text-sm font-semibold ${value === opt.key ? 'text-ma-navy' : 'text-gray-600'}`}>
            {opt.label}
          </div>
          <div className="text-xs text-gray-400 mt-0.5">{opt.desc}</div>
        </button>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function Income({ data, onUpdate }: Props) {
  const slPlan = data.studentLoanUndergrad || 'none';
  const isCouple = data.isCouple === true;

  // ── SINGLE CLIENT ──────────────────────────────────────────────────────────
  if (!isCouple) {
    return (
      <div className="p-6 max-w-3xl mx-auto">
        <div className="mb-6">
          <h2 className="text-2xl font-bold text-ma-navy">Income</h2>
          <p className="text-gray-500 mt-1 text-sm">
            Enter income details pre-session. These feed directly into the Tax Check screen.
          </p>
        </div>

        <div className="space-y-5">
          {/* Core income */}
          <div className="border rounded-2xl p-4 bg-blue-50/50 border-blue-200">
            <h3 className="text-sm font-semibold text-ma-navy mb-3">Earnings</h3>
            <div className="space-y-3">
              <CurrencyInput label="Annual salary" value={data.salary} onChange={v => onUpdate({ salary: v })} note="Director's salary — typically £12,570" />
              <CurrencyInput label="Annual dividends" value={data.dividends} onChange={v => onUpdate({ dividends: v })} note="Company dividends drawn" />
              <CurrencyInput label="Monthly expenses reimbursement" value={data.expensesReimbursement} onChange={v => onUpdate({ expensesReimbursement: v })} note="Tax-free pass-through — not taxed" />
              <CurrencyInput label="PAYE from other employment" value={data.payeIncome} onChange={v => onUpdate({ payeIncome: v })} note="Taxed as non-savings income — stacks first" />
              <CurrencyInput label="Net rental income (annual)" value={data.rentalIncomeNet} onChange={v => onUpdate({ rentalIncomeNet: v })} note="After allowable expenses — taxed as non-savings income" />
              <CurrencyInput label="Savings interest above Personal Savings Allowance (PSA)" value={data.savingsInterest} onChange={v => onUpdate({ savingsInterest: v })} note="Above £1,000 (basic rate) or £500 (higher rate) — stacks second" />
              <CurrencyInput label="Other dividends (annual)" value={data.otherDividends} onChange={v => onUpdate({ otherDividends: v })} note="From other shareholdings (above £500 allowance) — stacks last" />
            </div>
          </div>

          {/* Business */}
          <div className="border rounded-2xl p-4 bg-green-50/50 border-green-200">
            <h3 className="text-sm font-semibold text-ma-navy mb-3">Business</h3>
            <div className="space-y-3">
              <CurrencyInput label="Estimated company gross profit" value={data.estimatedCompanyProfit} onChange={v => onUpdate({ estimatedCompanyProfit: v })} note="Before salary, pension, and dividends. Used to calculate corporation tax rate." />
            </div>
          </div>

          {/* Student loan */}
          <div className="border rounded-2xl p-4 bg-purple-50/50 border-purple-200">
            <h3 className="text-sm font-semibold text-ma-navy mb-3">Student Loan</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Undergraduate student loan</label>
                <select
                  value={slPlan}
                  onChange={e => onUpdate({ studentLoanUndergrad: e.target.value as ClientData['studentLoanUndergrad'] })}
                  className="mt-1 w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-sm font-bold text-ma-navy outline-none focus:ring-2 focus:ring-ma-navy/20 focus:border-ma-navy/40"
                >
                  <option value="none">None</option>
                  <option value="plan1">Plan 1</option>
                  <option value="plan2">Plan 2</option>
                  <option value="plan5">Plan 5</option>
                </select>
              </div>
              {slPlan !== 'none' && (
                <div className="flex items-center justify-between">
                  <label className="text-xs text-gray-500 font-medium">Postgraduate loan?</label>
                  <button
                    onClick={() => onUpdate({ studentLoanPostgrad: !data.studentLoanPostgrad })}
                    className={`relative w-10 h-5 rounded-full transition ${data.studentLoanPostgrad ? 'bg-purple-500' : 'bg-gray-300'}`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition ${data.studentLoanPostgrad ? 'translate-x-5' : ''}`} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Tax payment method */}
          <div className="border rounded-2xl p-4 bg-gray-50/50 border-gray-200">
            <h3 className="text-sm font-semibold text-ma-navy mb-3">Tax Payment Method</h3>
            <TaxMethodPicker value={data.taxPaymentMethod || 'monthly'} onChange={v => onUpdate({ taxPaymentMethod: v })} />
            {data.taxPaymentMethod === 'monthly' && (
              <div className="mt-4">
                <CurrencyInput label="Monthly HMRC payment" value={data.monthlyHMRC} onChange={v => onUpdate({ monthlyHMRC: v })} note="Amount to pay HMRC each month" />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── COUPLE LAYOUT ──────────────────────────────────────────────────────────
  const clientName = data.clientName || 'Client';
  const partnerName = data.partnerName || 'Partner';

  const combinedGross =
    (data.salary || 0) + (data.dividends || 0) +
    (data.partnerSalary || 0) + (data.partnerDividends || 0);

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-ma-navy">Income</h2>
        <p className="text-gray-500 mt-1 text-sm">
          Enter income details for both {clientName} and {partnerName}.
        </p>
      </div>

      <div className="space-y-5">
        {/* ── Earnings — two columns ─────────────────────────────────── */}
        <div className="border rounded-2xl p-5 bg-blue-50/50 border-blue-200">
          <h3 className="text-sm font-semibold text-ma-navy mb-4">Earnings</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* CLIENT COLUMN */}
            <div className="space-y-3">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-600 pb-1 border-b border-blue-200 mb-2">{clientName}</div>
              <CurrencyInput label="Annual salary" value={data.salary} onChange={v => onUpdate({ salary: v })} note="Director's salary — typically £12,570" />
              <CurrencyInput label="Annual dividends" value={data.dividends} onChange={v => onUpdate({ dividends: v })} note="Company dividends drawn" />
              <CurrencyInput label="Other income (annual)" value={data.payeIncome} onChange={v => onUpdate({ payeIncome: v })} note="PAYE, freelance etc" />
              <CurrencyInput label="Expenses reimbursement (monthly)" value={data.expensesReimbursement} onChange={v => onUpdate({ expensesReimbursement: v })} note="Tax-free pass-through" />
            </div>

            {/* PARTNER COLUMN */}
            <div className="space-y-3">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-600 pb-1 border-b border-blue-200 mb-2">{partnerName}</div>
              <CurrencyInput label="Annual salary" value={data.partnerSalary || 0} onChange={v => onUpdate({ partnerSalary: v })} note="Director's salary — typically £12,570" />
              <CurrencyInput label="Annual dividends" value={data.partnerDividends || 0} onChange={v => onUpdate({ partnerDividends: v })} note="Company dividends drawn" />
              <CurrencyInput label="Other income (annual)" value={data.partnerOtherIncome || 0} onChange={v => onUpdate({ partnerOtherIncome: v })} note="PAYE, freelance etc" />
              <CurrencyInput label="Expenses reimbursement (monthly)" value={data.partnerExpensesReimbursement || 0} onChange={v => onUpdate({ partnerExpensesReimbursement: v })} note="Tax-free pass-through" />
            </div>
          </div>

          {/* Household-level income (not per-person) */}
          <div className="mt-4 pt-4 border-t border-blue-200 space-y-3">
            <CurrencyInput label="Net rental income (annual)" value={data.rentalIncomeNet} onChange={v => onUpdate({ rentalIncomeNet: v })} note="After allowable expenses — household total" />
            <CurrencyInput label="Savings interest above Personal Savings Allowance (PSA)" value={data.savingsInterest} onChange={v => onUpdate({ savingsInterest: v })} note="Above £1,000 (basic rate) or £500 (higher rate)" />
            <CurrencyInput label="Other dividends (annual)" value={data.otherDividends} onChange={v => onUpdate({ otherDividends: v })} note="From other shareholdings (above £500 allowance)" />
          </div>

          {/* Combined summary */}
          <div className="bg-ma-navy rounded-xl p-4 mt-4">
            <div className="flex justify-between items-center">
              <span className="text-white/70 text-sm">Combined household gross income</span>
              <span className="text-white text-lg font-bold">{fmt(combinedGross)}</span>
            </div>
          </div>
        </div>

        {/* ── Tax payment method — two columns ───────────────────────── */}
        <div className="border rounded-2xl p-5 bg-gray-50/50 border-gray-200">
          <h3 className="text-sm font-semibold text-ma-navy mb-4">Tax Payment Method</h3>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
            {/* CLIENT */}
            <div className="space-y-3">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-600 pb-1 border-b border-gray-200 mb-2">{clientName}</div>
              <TaxMethodPicker value={data.taxPaymentMethod || 'monthly'} onChange={v => onUpdate({ taxPaymentMethod: v })} />
              {data.taxPaymentMethod === 'monthly' && (
                <CurrencyInput label="Monthly HMRC payment" value={data.monthlyHMRC} onChange={v => onUpdate({ monthlyHMRC: v })} note="Amount to pay HMRC each month" />
              )}
            </div>

            {/* PARTNER */}
            <div className="space-y-3">
              <div className="text-xs font-bold uppercase tracking-wide text-gray-600 pb-1 border-b border-gray-200 mb-2">{partnerName}</div>
              <TaxMethodPicker value={data.partnerTaxPaymentMethod || 'none'} onChange={v => onUpdate({ partnerTaxPaymentMethod: v })} />
              {data.partnerTaxPaymentMethod === 'monthly' && (
                <CurrencyInput label="Monthly HMRC payment" value={data.partnerMonthlyHMRC || 0} onChange={v => onUpdate({ partnerMonthlyHMRC: v })} note="Amount to pay HMRC each month" />
              )}
            </div>
          </div>
        </div>

        {/* ── Business (client only — not duplicated for partner) ─────── */}
        <div className="border rounded-2xl p-4 bg-green-50/50 border-green-200">
          <h3 className="text-sm font-semibold text-ma-navy mb-3">Business</h3>
          <div className="space-y-3">
            <CurrencyInput label="Estimated company gross profit" value={data.estimatedCompanyProfit} onChange={v => onUpdate({ estimatedCompanyProfit: v })} note="Before salary, pension, and dividends. Used to calculate corporation tax rate." />
          </div>
        </div>

        {/* ── Student loan (client only) ──────────────────────────────── */}
        <div className="border rounded-2xl p-4 bg-purple-50/50 border-purple-200">
          <h3 className="text-sm font-semibold text-ma-navy mb-3">Student Loan</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-gray-400 uppercase tracking-wide font-medium">Undergraduate student loan</label>
              <select
                value={slPlan}
                onChange={e => onUpdate({ studentLoanUndergrad: e.target.value as ClientData['studentLoanUndergrad'] })}
                className="mt-1 w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-2 text-sm font-bold text-ma-navy outline-none focus:ring-2 focus:ring-ma-navy/20 focus:border-ma-navy/40"
              >
                <option value="none">None</option>
                <option value="plan1">Plan 1</option>
                <option value="plan2">Plan 2</option>
                <option value="plan5">Plan 5</option>
              </select>
            </div>
            {slPlan !== 'none' && (
              <div className="flex items-center justify-between">
                <label className="text-xs text-gray-500 font-medium">Postgraduate loan?</label>
                <button
                  onClick={() => onUpdate({ studentLoanPostgrad: !data.studentLoanPostgrad })}
                  className={`relative w-10 h-5 rounded-full transition ${data.studentLoanPostgrad ? 'bg-purple-500' : 'bg-gray-300'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition ${data.studentLoanPostgrad ? 'translate-x-5' : ''}`} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
